import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Button,
  Space,
  DatePicker,
  Divider,
  Select
} from 'antd';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apiCheckDeviceExist,
  apiCreateReplacement,
  apiGetReplacementById,
  apiUpdateReplacement
} from '@services/ManageDevices/DeviceServices';
import { apiGetMasterData } from '@services/MasterData/MasterDataService';
import { showSuccess, showWarning } from '@components/notification';
import CustomerSelect from '@components/Select/SelectCustomer';
import DeviceSelect from '@components/Select/SelectDevice';
import dayjs from 'dayjs';
import { ArrowBack } from '@mui/icons-material';
import TextArea from 'antd/es/input/TextArea';
import CustomerSelectByReplacementType from '@components/Select/SelectCustomerByeReplacementType';
import InputUploadImage from '@components/Upload/Upload';
import moment from 'moment';

const { Title } = Typography;
const { Option } = Select;

export const TYPES = [
  { label: 'Lắp mới/ lắp lại Module', value: 'NEW' },
  { label: 'Thay thế Module', value: 'REPLACE' },
  { label: 'Thay thế đồng hồ', value: 'REPLACE_METER' },
  { label: 'Thay thế Module & Đồng hồ', value: 'REPLACE_ALL' },
  { label: 'Gỡ bỏ Module', value: 'REMOVE' },
  { label: 'Lắp mới / lắp lại đồng hồ', value: 'INSTALL_METER' },
  { label: 'Gỡ bỏ đồng hồ', value: 'REMOVE_METER' }
];

const PULSE_RATIO_OPTIONS = [
  { value: '1', label: '1Lít/Xung' },
  { value: '2', label: '10Lít/Xung' },
  { value: '3', label: '100Lít/Xung' },
  { value: '4', label: '1000Lít/Xung' },
  { value: '5', label: '0.5Lít/Xung' },
  { value: '6', label: '5Lít/Xung' },
  { value: '9', label: '3Lít/Xung' },
  { value: '10', label: '30Lít/Xung' }
];

const MAX_READING_OPTIONS = [
  { value: '999999', label: '999,999' },
  { value: '9999999', label: '9,999,999' },
  { value: '99999999', label: '99,999,999' },
  { value: '999999999', label: '999,999,999' },
  { value: '9999999999', label: '9,999,999,999' },
  { value: '99999999999', label: '99,999,999,999' },
  { value: '999999999999', label: '999,999,999,999' }
];

const DeviceOperator = ({ serialNo }) => {
  const [operator, setOperator] = useState('');

  useEffect(() => {
    let cancelled = false;
    setOperator('');

    if (serialNo) {
      apiCheckDeviceExist({ serial_number: serialNo })
        .then((res) => {
          if (!cancelled) setOperator(res.data?.data?.operator ?? '');
        })
        .catch(() => {
          if (!cancelled) setOperator('');
        });
    }

    return () => {
      cancelled = true;
    };
  }, [serialNo]);

  return (
    <>
      <Title style={{ fontSize: '15px', fontWeight: '400' }}>Nhà mạng</Title>
      <Input value={operator || '-'} disabled />
    </>
  );
};

// Validation schema with necessary fields
const validationSchema = (type) => {
  switch (type) {
    case 'NEW':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        serial_no_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        model_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        imei_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_new: Yup.string()
          .matches(/^\d+$/, 'Chỉ số đồng hồ phải là số!')
          .required('Trường này là bắt buộc nhập!'),
        reason: Yup.string().required('Trường này là bắt buộc nhập!'),
        new_device_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh thiết bị mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_reading_new_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable()
      });
    case 'REPLACE':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        serial_no_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        model_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        imei_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        // meter_reading_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        serial_no_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        model_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        imei_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_new: Yup.string()
          .matches(/^\d+$/, 'Chỉ số đồng hồ phải là số!')
          .required('Trường này là bắt buộc nhập!'),
        reason: Yup.string().required('Trường này là bắt buộc nhập!'),
        new_device_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh thiết bị mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_reading_new_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable()
      });
    case 'REPLACE_METER':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        meter_serial_no_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_name_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_size_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        pulse_ratio_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        max_reading_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_new: Yup.string()
          .matches(/^\d+$/, 'Chỉ số đồng hồ phải là số!')
          .required('Trường này là bắt buộc nhập!'),
        reason: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_serial_image_new: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh seri đồng hồ mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_reading_new_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable()
      });
    case 'REPLACE_ALL':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        serial_no_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        model_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        imei_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_old: Yup.string()
          .matches(/^\d+$/, 'Chỉ số đồng hồ phải là số!')
          .required('Trường này là bắt buộc nhập!'),
        serial_no_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        model_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        imei_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_serial_no_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_name_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_size_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        pulse_ratio_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        max_reading_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_new: Yup.string()
          .matches(/^\d+$/, 'Chỉ số đồng hồ phải là số!')
          .required('Trường này là bắt buộc nhập!'),
        reason: Yup.string().required('Trường này là bắt buộc nhập!'),
        old_device_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh thiết bị cũ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_reading_old_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ cũ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_serial_image_new: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh seri đồng hồ mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_reading_new_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ mới!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable()
      });
    case 'REMOVE':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        // meter_reading_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        serial_no_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        // model_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        // imei_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        reason: Yup.string().required('Trường này là bắt buộc nhập!'),
        old_device_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh thiết bị cũ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_reading_old_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ cũ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable()
      });
    case 'INSTALL_METER':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        meter_serial_no_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_name_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_size_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        pulse_ratio_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        max_reading_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_new: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_new_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_serial_image_new: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh seri đồng hồ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        reason: Yup.string().required('Trường này là bắt buộc nhập!')
      });
    case 'REMOVE_METER':
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable(),
        meter_reading_old: Yup.string().required('Trường này là bắt buộc nhập!'),
        meter_reading_old_image: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh chỉ số đồng hồ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        meter_serial_image_old: Yup.object()
          .test(
            'is-not-empty',
            'Vui lòng nhập hình ảnh seri đồng hồ!',
            (value) => value && Object.keys(value).length > 0
          )
          .nullable(),
        reason: Yup.string().required('Trường này là bắt buộc nhập!')
      });
    default:
      return Yup.object({
        customer_id: Yup.string().required('Trường này là bắt buộc nhập!').nullable(),
        replacement_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable()
      });
  }
};

export const Add = ({ disabled }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [replacement, setReplacement] = useState({});
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [selectedType, setSelectedType] = useState('NEW');
  const [meterNameOptions, setMeterNameOptions] = useState([]);
  const [meterSizeOptions, setMeterSizeOptions] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  const [componentKey, setComponentKey] = useState(0);
  const [defaultReplacementDate] = useState(() => dayjs());

  const handleSubmit = async (values) => {
    try {
      // showWarning({ message: 'Tính năng đang liên tục phát triển, vui lòng quay lại sau!' });
      setLoading(true);
      const replacement_date = dayjs(values?.replacement_date).format('YYYY-MM-DD HH:mm:ss');
      id
        ? await apiUpdateReplacement({ ...values, replacement_date }, id)
        : await apiCreateReplacement({ ...values, replacement_date });
      showSuccess({ message: 'Thành công!' });
      navigate('/replacements');
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeType = (value) => {
    setSelectedType(value);
  };

  const fetch = async () => {
    try {
      setLoadingFetch(true);
      const res = await apiGetReplacementById(id);
      if (res) {
        setReplacement(res.data.data);
        setSelectedType(res?.data?.data?.type);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingFetch(false);
    }
  };

  const fetchMeterMasterData = async () => {
    try {
      setLoadingMasterData(true);
      const [nameRes, sizeRes] = await Promise.all([
        apiGetMasterData({ type: 'METER_NAME', page: 1, size: 200 }),
        apiGetMasterData({ type: 'METER_SIZE', page: 1, size: 200 })
      ]);
      setMeterNameOptions(
        (nameRes?.data?.data ?? []).map((item) => ({ label: item.name, value: item.value }))
      );
      setMeterSizeOptions(
        (sizeRes?.data?.data ?? []).map((item) => ({ label: item.name, value: item.value }))
      );
    } catch (e) {
      console.error('[fetchMeterMasterData]', e);
    } finally {
      setLoadingMasterData(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetch();
    }
  }, [id]);

  useEffect(() => {
    if (
      selectedType === 'INSTALL_METER' ||
      selectedType === 'REPLACE_METER' ||
      selectedType === 'REPLACE_ALL' ||
      selectedType === 'REMOVE_METER'
    ) {
      fetchMeterMasterData();
    }
  }, [selectedType]);

  const isNew = selectedType === 'NEW';
  const isReplace =
    selectedType === 'REPLACE' ||
    selectedType === 'REPLACE_METER' ||
    selectedType === 'REPLACE_ALL';
  const isRemove = selectedType === 'REMOVE';
  const isInstallMeter = selectedType === 'INSTALL_METER';
  const isRemoveMeter = selectedType === 'REMOVE_METER';

  const isReplaceMeter = selectedType === 'REPLACE_METER' || selectedType === 'REPLACE_ALL';

  const isReplaceOnlyMeter = selectedType === 'REPLACE_METER';

  const initialValues = useMemo(
    () => ({
      type: replacement?.type ?? 'NEW',
      customer_id: replacement?.customer_id ?? '',
      customer_name: replacement?.customer_name ?? '',
      replacement_date: replacement?.replacement_date
        ? dayjs(moment(replacement?.replacement_date).utc().format('YYYY-MM-DD HH:mm'))
        : defaultReplacementDate,
      serial_no_old: replacement?.serial_no_old ?? '',
      model_old: replacement?.model_old ?? '',
      imei_old: replacement?.imei_old ?? '',
      old_device_image: replacement?.old_device_image ?? {},
      meter_reading_old: replacement?.meter_reading_old ?? '',
      meter_reading_old_image: replacement?.meter_reading_old_image ?? {},
      serial_no_new: replacement?.serial_no_new ?? '',
      model_new: replacement?.model_new ?? '',
      imei_new: replacement?.imei_new ?? '',
      new_device_image: replacement?.new_device_image ?? {},
      meter_reading_new: replacement?.meter_reading_new ?? '',
      meter_reading_new_image: replacement?.meter_reading_new_image ?? {},
      reason: replacement?.reason ?? '',
      meter_serial_no_old: replacement?.meter_serial_no_old ?? '',
      meter_name_old: replacement?.meter_name_old ?? '',
      meter_size_old: replacement?.meter_size_old ?? '',
      pulse_ratio_old: replacement?.pulse_ratio_old ?? '',
      max_reading_old: replacement?.max_reading_old ?? '',
      meter_serial_image_old: replacement?.meter_serial_image_old ?? {},
      meter_serial_no_new: replacement?.meter_serial_no_new ?? '',
      meter_name_new: replacement?.meter_name_new ?? '',
      meter_size_new: replacement?.meter_size_new ?? '',
      pulse_ratio_new: replacement?.pulse_ratio_new ?? '',
      max_reading_new: replacement?.max_reading_new ?? '',
      meter_serial_image_new: replacement?.meter_serial_image_new ?? {}
    }),
    [defaultReplacementDate, replacement]
  );

  return (
    <Card
      title={id ? 'Cập nhật thông tin' : 'Khai báo thông tin'}
      loading={loadingFetch}
      bodyStyle={{ padding: '24px', paddingTop: '0px' }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema(selectedType)}
        onSubmit={handleSubmit}>
        {({ setFieldValue, values, errors }) => {
          console.log('eorrr:::', errors);

          return (
            <Form>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col md={20} xs={24}>
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col md={8} xs={24}>
                      <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                        Chọn loại sự kiện*
                      </Title>
                      <Field name="type">
                        {({ field }) => (
                          <Select
                            disabled={disabled}
                            {...field}
                            style={{ width: '100%', height: '36px' }}
                            placeholder="Lựa chọn"
                            value={selectedType}
                            onChange={(value) => {
                              setSelectedType(value);
                              setFieldValue('type', value);
                              if (!id) {
                                setFieldValue('customer_id', null);
                              }
                            }}>
                            {TYPES.map((type) => (
                              <Option key={type.value} value={type.value}>
                                {type.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Field>
                    </Col>
                    <Col md={8} xs={24}>
                      <Title style={{ fontSize: '15px', fontWeight: '400' }}>Thời điểm*</Title>
                      <DatePicker
                        value={values?.replacement_date}
                        style={{ width: '100%' }}
                        onChange={(date) => {
                          setFieldValue('replacement_date', date);
                          if (!id) {
                            if (values?.type !== 'REMOVE') {
                              setFieldValue('customer_id', null);
                              // Reset component key để clear UI
                              setComponentKey((prev) => prev + 1);
                            }
                          }
                        }}
                        placeholder="Chọn"
                        format="YYYY-MM-DD HH:mm"
                        showTime={{ use12Hours: false }}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                        disabledTime={(current) => {
                          if (!current || !current.isSame(dayjs(), 'day')) {
                            return {};
                          }

                          const now = dayjs();

                          return {
                            disabledHours: () =>
                              Array.from({ length: 24 }, (_, hour) => hour).filter(
                                (hour) => hour > now.hour()
                              ),
                            disabledMinutes: (selectedHour) => {
                              if (selectedHour !== now.hour()) {
                                return [];
                              }

                              return Array.from({ length: 60 }, (_, minute) => minute).filter(
                                (minute) => minute > now.minute()
                              );
                            }
                          };
                        }}
                        disabled={disabled}
                      />
                      <ErrorMessage
                        name="replacement_date"
                        component="div"
                        style={{ color: '#e11d48' }}
                      />
                    </Col>
                    <Col md={16} xs={24}>
                      <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                        Chọn khách hàng*
                      </Title>
                      <Field name="customer_id">
                        {({ field }) => (
                          <CustomerSelectByReplacementType
                            {...field}
                            disabled={disabled || !!id}
                            initValue={replacement?.customer_id ?? field?.value}
                            type={id ? undefined : selectedType}
                            date={id ? undefined : values?.replacement_date}
                            onChange={(option) => {
                              setFieldValue('customer_id', option?.value);
                              setFieldValue('customer_name', option?.title?.name);
                              if (!id) {
                                if (isReplace || isRemove) {
                                  setFieldValue('serial_no_old', option?.title?.serial_no_new);
                                  setFieldValue('model_old', option?.title?.model_new);
                                  setFieldValue('imei_old', option?.title?.imei_new);
                                }
                                if (selectedType === 'REPLACE_METER') {
                                  setFieldValue('serial_no_new', option?.title?.serial_no_new);
                                  setFieldValue('model_new', option?.title?.model_new);
                                  setFieldValue('imei_new', option?.title?.imei_new);
                                }
                                if (isReplaceMeter) {
                                  setFieldValue(
                                    'meter_serial_no_old',
                                    option?.title?.meter_serial_no_new ?? ''
                                  );
                                  setFieldValue(
                                    'meter_name_old',
                                    option?.title?.meter_name_new ?? ''
                                  );
                                  setFieldValue(
                                    'meter_size_old',
                                    option?.title?.meter_size_new ?? ''
                                  );
                                  setFieldValue(
                                    'pulse_ratio_old',
                                    option?.title?.pulse_ratio_new ?? ''
                                  );
                                  setFieldValue(
                                    'max_reading_old',
                                    option?.title?.max_reading_new ?? ''
                                  );
                                }
                                if (isRemoveMeter) {
                                  if (option?.title?.has_active_module) {
                                    showWarning({
                                      message:
                                        'Khách hàng đang lắp Module. Vui lòng gỡ bỏ Module trước khi gỡ đồng hồ!'
                                    });
                                    setFieldValue('customer_id', null);
                                    return;
                                  }
                                  setFieldValue(
                                    'meter_serial_no_old',
                                    option?.title?.meter_serial_no_new ?? ''
                                  );
                                  setFieldValue(
                                    'meter_name_old',
                                    option?.title?.meter_name_new ?? ''
                                  );
                                  setFieldValue(
                                    'meter_size_old',
                                    option?.title?.meter_size_new ?? ''
                                  );
                                  setFieldValue(
                                    'pulse_ratio_old',
                                    option?.title?.pulse_ratio_new ?? ''
                                  );
                                  setFieldValue(
                                    'max_reading_old',
                                    option?.title?.max_reading_new ?? ''
                                  );
                                }
                              }
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="customer_id" component="div" style={{ color: 'red' }} />
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    {/* Left Column for Old Device Information */}
                    {(isReplace || isRemove) && !isReplaceOnlyMeter && (
                      <Col md={16} xs={24}>
                        {!isRemove && (
                          <Divider
                            style={{
                              borderColor: '#0284c7',
                              borderWidth: '1px',
                              color: '#0284c7',
                              transform: undefined,
                              marginBottom: 0
                            }}>
                            Thông tin thiết bị cũ
                          </Divider>
                        )}

                        <Row gutter={[16, 8]} style={{ paddingTop: 0 }}>
                          {!isRemove && (
                            <>
                              <Col md={24} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Số Serial*
                                </Title>
                                <Field name="serial_no_old">
                                  {({ field }) => (
                                    <DeviceSelect
                                      key={componentKey}
                                      {...field}
                                      placeholder="Tự động điền (Vui lòng chọn khách hàng trước)"
                                      disabled={id ? false : disabled || isReplace || isRemove}
                                      initValue={replacement?.serial_no_old ?? field?.value}
                                      onChange={(option) => {
                                        setFieldValue('serial_no_old', option?.value);
                                        setFieldValue('model_old', option?.title);
                                        setFieldValue('imei_old', option?.key);
                                      }}
                                    />
                                  )}
                                </Field>
                                <ErrorMessage
                                  name="serial_no_old"
                                  component="div"
                                  style={{ color: '#e11d48' }}
                                />
                              </Col>
                              <Col md={12} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Model*
                                </Title>
                                <Field name="model_old">
                                  {({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder="Tự động điền"
                                      disabled={disabled || isReplace || isRemove}
                                    />
                                  )}
                                </Field>
                                <ErrorMessage
                                  name="model_old"
                                  component="div"
                                  style={{ color: '#e11d48' }}
                                />
                              </Col>
                              <Col md={12} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>IMEI*</Title>
                                <Field name="imei_old">
                                  {({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder="Tự động điền"
                                      disabled={disabled || isReplace || isRemove}
                                    />
                                  )}
                                </Field>
                                <ErrorMessage
                                  name="imei_old"
                                  component="div"
                                  style={{ color: '#e11d48' }}
                                />
                              </Col>
                              <Col md={12} xs={24}>
                                <DeviceOperator serialNo={values.serial_no_old} />
                              </Col>
                            </>
                          )}

                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              {isRemove ? 'Ảnh thiết bị cũ' : 'Ảnh thiết bị'}
                            </Title>
                            <Field name="old_device_image">
                              {({ field }) => (
                                <InputUploadImage
                                  key={componentKey}
                                  {...field}
                                  initValue={replacement?.old_device_image ?? ''}
                                  onChange={(file) => setFieldValue('old_device_image', file)}
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="old_device_image"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    )}

                    {(isReplaceMeter || isRemove) && (
                      <Col md={16} xs={24}>
                        {!isRemove && (
                          <Divider
                            style={{
                              borderColor: '#0284c7',
                              borderTopWidth: '1px',
                              color: '#0284c7',
                              marginBottom: 0
                            }}>
                            {isReplaceOnlyMeter || isReplaceMeter ? 'Đồng hồ cũ' : 'Chỉ số cũ'}
                          </Divider>
                        )}

                        <Row gutter={[16, 8]}>
                          {isReplaceMeter && (
                            <>
                              <Col md={24} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Số Serial đồng hồ cũ
                                </Title>
                                <Field name="meter_serial_no_old">
                                  {({ field }) => (
                                    <Input {...field} placeholder="Tự động điền" disabled />
                                  )}
                                </Field>
                              </Col>
                              <Col md={12} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Tên đồng hồ cũ
                                </Title>
                                <Field name="meter_name_old">
                                  {({ field }) => (
                                    <Select
                                      {...field}
                                      style={{ width: '100%' }}
                                      placeholder="Tự động điền"
                                      loading={loadingMasterData}
                                      showSearch
                                      optionFilterProp="label"
                                      options={meterNameOptions}
                                      value={field.value || undefined}
                                      disabled
                                    />
                                  )}
                                </Field>
                              </Col>
                              <Col md={12} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Cỡ đồng hồ cũ
                                </Title>
                                <Field name="meter_size_old">
                                  {({ field }) => (
                                    <Select
                                      {...field}
                                      style={{ width: '100%' }}
                                      placeholder="Tự động điền"
                                      loading={loadingMasterData}
                                      showSearch
                                      optionFilterProp="label"
                                      options={meterSizeOptions}
                                      value={field.value || undefined}
                                      disabled
                                    />
                                  )}
                                </Field>
                              </Col>
                              <Col md={12} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Hệ số xung cũ
                                </Title>
                                <Field name="pulse_ratio_old">
                                  {({ field }) => (
                                    <Select
                                      {...field}
                                      style={{ width: '100%' }}
                                      placeholder="Tự động điền"
                                      value={field.value || undefined}
                                      disabled>
                                      {PULSE_RATIO_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  )}
                                </Field>
                              </Col>
                              <Col md={12} xs={24}>
                                <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                  Chỉ số cực đại cũ
                                </Title>
                                <Field name="max_reading_old">
                                  {({ field }) => (
                                    <Select
                                      {...field}
                                      style={{ width: '100%' }}
                                      placeholder="Tự động điền"
                                      value={field.value || undefined}
                                      disabled>
                                      {MAX_READING_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  )}
                                </Field>
                              </Col>
                            </>
                          )}
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Chỉ số đồng hồ (lít)*
                            </Title>
                            <Field name="meter_reading_old">
                              {({ field }) => (
                                <Input {...field} placeholder="Nhập dữ liệu" disabled={disabled} />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_reading_old"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Ảnh chỉ số đồng hồ
                            </Title>
                            <Field name="meter_reading_old_image">
                              {({ field }) => (
                                <InputUploadImage
                                  key={componentKey}
                                  {...field}
                                  initValue={replacement?.meter_reading_old_image ?? ''}
                                  onChange={(file) =>
                                    setFieldValue('meter_reading_old_image', file)
                                  }
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_reading_old_image"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          {isReplaceOnlyMeter && (
                            <Col md={24} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                Ảnh thiết bị
                              </Title>
                              <Field name="old_device_image">
                                {({ field }) => (
                                  <InputUploadImage
                                    key={componentKey}
                                    {...field}
                                    initValue={replacement?.old_device_image ?? ''}
                                    onChange={(file) => setFieldValue('old_device_image', file)}
                                    disabled={disabled}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="old_device_image"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                          )}
                        </Row>
                      </Col>
                    )}

                    {/* Right Column for New Device Information */}
                    {!isRemove && !isReplaceOnlyMeter && !isInstallMeter && !isRemoveMeter && (
                      <>
                        <Col md={16} xs={24}>
                          <Divider
                            style={{
                              borderColor: '#0284c7',
                              borderTopWidth: '1px',
                              color: '#0284c7',
                              marginBottom: 0
                            }}>
                            Thông tin thiết bị mới
                          </Divider>
                          <Row gutter={[16, 8]}>
                            <Col md={24} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                Số Serial*
                              </Title>
                              <Field name="serial_no_new">
                                {({ field }) => (
                                  <DeviceSelect
                                    key={componentKey}
                                    {...field}
                                    disabled={disabled}
                                    initValue={replacement?.serial_no_new ?? field?.value}
                                    type={id ? undefined : 'NEW'}
                                    onChange={(option) => {
                                      setFieldValue('serial_no_new', option?.value);
                                      setFieldValue('model_new', option?.title);
                                      setFieldValue('imei_new', option?.key);
                                    }}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="serial_no_new"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                            <Col md={12} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>Model*</Title>
                              <Field name="model_new">
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="Tự động điền"
                                    disabled={disabled}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="model_new"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                            <Col md={12} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>IMEI*</Title>
                              <Field name="imei_new">
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="Tự động điền"
                                    disabled={disabled}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="imei_new"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                            <Col md={12} xs={24}>
                              <DeviceOperator serialNo={values.serial_no_new} />
                            </Col>
                            <Col md={24} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                Ảnh thiết bị
                              </Title>
                              <Field name="new_device_image">
                                {({ field }) => (
                                  <InputUploadImage
                                    key={componentKey}
                                    {...field}
                                    initValue={replacement?.new_device_image ?? ''}
                                    onChange={(file) => setFieldValue('new_device_image', file)}
                                    disabled={disabled}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="new_device_image"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                          </Row>
                        </Col>
                      </>
                    )}
                    {!isRemove && !isInstallMeter && !isRemoveMeter && (
                      <>
                        <Col md={16} xs={24}>
                          {(isReplaceOnlyMeter || isReplaceMeter) && (
                            <Divider
                              style={{
                                borderColor: '#0284c7',
                                borderTopWidth: '1px',
                                color: '#0284c7',
                                marginBottom: 0
                              }}>
                              Đồng hồ mới
                            </Divider>
                          )}

                          <Row gutter={[16, 8]}>
                            {isReplaceMeter && (
                              <>
                                <Col md={24} xs={24}>
                                  <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                    Số Serial đồng hồ mới*
                                  </Title>
                                  <Field name="meter_serial_no_new">
                                    {({ field }) => (
                                      <Input
                                        {...field}
                                        placeholder="Nhập số serial đồng hồ mới"
                                        disabled={disabled}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name="meter_serial_no_new"
                                    component="div"
                                    style={{ color: '#e11d48' }}
                                  />
                                </Col>
                                <Col md={12} xs={24}>
                                  <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                    Tên đồng hồ mới*
                                  </Title>
                                  <Field name="meter_name_new">
                                    {({ field }) => (
                                      <Select
                                        {...field}
                                        style={{ width: '100%' }}
                                        placeholder="Chọn tên đồng hồ"
                                        loading={loadingMasterData}
                                        showSearch
                                        optionFilterProp="label"
                                        options={meterNameOptions}
                                        value={field.value || undefined}
                                        onChange={(value) => setFieldValue('meter_name_new', value)}
                                        disabled={disabled}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name="meter_name_new"
                                    component="div"
                                    style={{ color: '#e11d48' }}
                                  />
                                </Col>
                                <Col md={12} xs={24}>
                                  <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                    Cỡ đồng hồ mới*
                                  </Title>
                                  <Field name="meter_size_new">
                                    {({ field }) => (
                                      <Select
                                        {...field}
                                        style={{ width: '100%' }}
                                        placeholder="Chọn cỡ đồng hồ"
                                        loading={loadingMasterData}
                                        showSearch
                                        optionFilterProp="label"
                                        options={meterSizeOptions}
                                        value={field.value || undefined}
                                        onChange={(value) => setFieldValue('meter_size_new', value)}
                                        disabled={disabled}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name="meter_size_new"
                                    component="div"
                                    style={{ color: '#e11d48' }}
                                  />
                                </Col>
                                <Col md={12} xs={24}>
                                  <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                    Hệ số xung mới*
                                  </Title>
                                  <Field name="pulse_ratio_new">
                                    {({ field }) => (
                                      <Select
                                        {...field}
                                        style={{ width: '100%' }}
                                        placeholder="Chọn hệ số xung"
                                        value={field.value || undefined}
                                        onChange={(value) =>
                                          setFieldValue('pulse_ratio_new', value)
                                        }
                                        disabled={disabled}>
                                        {PULSE_RATIO_OPTIONS.map((opt) => (
                                          <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </Option>
                                        ))}
                                      </Select>
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name="pulse_ratio_new"
                                    component="div"
                                    style={{ color: '#e11d48' }}
                                  />
                                </Col>
                                <Col md={12} xs={24}>
                                  <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                    Chỉ số cực đại mới*
                                  </Title>
                                  <Field name="max_reading_new">
                                    {({ field }) => (
                                      <Select
                                        {...field}
                                        style={{ width: '100%' }}
                                        placeholder="Chọn chỉ số cực đại"
                                        value={field.value || undefined}
                                        onChange={(value) =>
                                          setFieldValue('max_reading_new', value)
                                        }
                                        disabled={disabled}>
                                        {MAX_READING_OPTIONS.map((opt) => (
                                          <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </Option>
                                        ))}
                                      </Select>
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name="max_reading_new"
                                    component="div"
                                    style={{ color: '#e11d48' }}
                                  />
                                </Col>
                                <Col md={24} xs={24}>
                                  <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                    Ảnh seri đồng hồ mới*
                                  </Title>
                                  <Field name="meter_serial_image_new">
                                    {({ field }) => (
                                      <InputUploadImage
                                        key={componentKey}
                                        {...field}
                                        initValue={replacement?.meter_serial_image_new ?? ''}
                                        onChange={(file) =>
                                          setFieldValue('meter_serial_image_new', file)
                                        }
                                        disabled={disabled}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    name="meter_serial_image_new"
                                    component="div"
                                    style={{ color: '#e11d48' }}
                                  />
                                </Col>
                              </>
                            )}
                            <Col md={24} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                Chỉ số đồng hồ (lít)*
                              </Title>
                              <Field name="meter_reading_new">
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="Nhập dữ liệu"
                                    disabled={disabled}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="meter_reading_new"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                            <Col md={24} xs={24}>
                              <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                                Ảnh chỉ số đồng hồ
                              </Title>
                              <Field name="meter_reading_new_image">
                                {({ field }) => (
                                  <InputUploadImage
                                    key={componentKey}
                                    {...field}
                                    initValue={replacement?.meter_reading_new_image ?? ''}
                                    onChange={(file) =>
                                      setFieldValue('meter_reading_new_image', file)
                                    }
                                    disabled={disabled}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name="meter_reading_new_image"
                                component="div"
                                style={{ color: '#e11d48' }}
                              />
                            </Col>
                          </Row>
                        </Col>
                      </>
                    )}

                    {/* Thông tin đồng hồ mới — chỉ hiển thị khi INSTALL_METER */}
                    {isInstallMeter && (
                      <Col md={16} xs={24}>
                        <Divider
                          style={{
                            borderColor: '#0284c7',
                            borderTopWidth: '1px',
                            color: '#0284c7',
                            marginBottom: 0
                          }}>
                          Thông tin đồng hồ mới
                        </Divider>
                        <Row gutter={[16, 8]}>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Số Serial*
                            </Title>
                            <Field name="meter_serial_no_new">
                              {({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="Nhập số serial đồng hồ"
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_serial_no_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Tên đồng hồ*
                            </Title>
                            <Field name="meter_name_new">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Chọn tên đồng hồ"
                                  loading={loadingMasterData}
                                  showSearch
                                  optionFilterProp="label"
                                  options={meterNameOptions}
                                  value={field.value || undefined}
                                  onChange={(value) => setFieldValue('meter_name_new', value)}
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_name_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Cỡ đồng hồ*
                            </Title>
                            <Field name="meter_size_new">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Chọn cỡ đồng hồ"
                                  loading={loadingMasterData}
                                  showSearch
                                  optionFilterProp="label"
                                  options={meterSizeOptions}
                                  value={field.value || undefined}
                                  onChange={(value) => setFieldValue('meter_size_new', value)}
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_size_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Hệ số xung*
                            </Title>
                            <Field name="pulse_ratio_new">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Chọn hệ số xung"
                                  value={field.value || undefined}
                                  onChange={(value) => setFieldValue('pulse_ratio_new', value)}
                                  disabled={disabled}>
                                  {PULSE_RATIO_OPTIONS.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Option>
                                  ))}
                                </Select>
                              )}
                            </Field>
                            <ErrorMessage
                              name="pulse_ratio_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Chỉ số cực đại*
                            </Title>
                            <Field name="max_reading_new">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Chọn chỉ số cực đại"
                                  value={field.value || undefined}
                                  onChange={(value) => setFieldValue('max_reading_new', value)}
                                  disabled={disabled}>
                                  {MAX_READING_OPTIONS.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Option>
                                  ))}
                                </Select>
                              )}
                            </Field>
                            <ErrorMessage
                              name="max_reading_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Chỉ số (lít)*
                            </Title>
                            <Field name="meter_reading_new">
                              {({ field }) => (
                                <Input {...field} placeholder="Nhập chỉ số" disabled={disabled} />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_reading_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Ảnh chỉ số đồng hồ*
                            </Title>
                            <Field name="meter_reading_new_image">
                              {({ field }) => (
                                <InputUploadImage
                                  key={componentKey}
                                  {...field}
                                  initValue={replacement?.meter_reading_new_image ?? ''}
                                  onChange={(file) =>
                                    setFieldValue('meter_reading_new_image', file)
                                  }
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_reading_new_image"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Ảnh seri đồng hồ*
                            </Title>
                            <Field name="meter_serial_image_new">
                              {({ field }) => (
                                <InputUploadImage
                                  key={componentKey}
                                  {...field}
                                  initValue={replacement?.meter_serial_image_new ?? ''}
                                  onChange={(file) => setFieldValue('meter_serial_image_new', file)}
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_serial_image_new"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    )}

                    {/* Gỡ bỏ đồng hồ */}
                    {isRemoveMeter && (
                      <Col md={16} xs={24}>
                        <Divider
                          style={{
                            borderColor: '#0284c7',
                            borderTopWidth: '1px',
                            color: '#0284c7',
                            marginBottom: 0
                          }}>
                          Thông tin đồng hồ gỡ bỏ
                        </Divider>
                        <Row gutter={[16, 8]}>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Số Serial đồng hồ
                            </Title>
                            <Field name="meter_serial_no_old">
                              {({ field }) => (
                                <Input {...field} placeholder="Tự động điền" disabled />
                              )}
                            </Field>
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Tên đồng hồ
                            </Title>
                            <Field name="meter_name_old">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Tự động điền"
                                  loading={loadingMasterData}
                                  showSearch
                                  optionFilterProp="label"
                                  options={meterNameOptions}
                                  value={field.value || undefined}
                                  disabled
                                />
                              )}
                            </Field>
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Cỡ đồng hồ
                            </Title>
                            <Field name="meter_size_old">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Tự động điền"
                                  loading={loadingMasterData}
                                  showSearch
                                  optionFilterProp="label"
                                  options={meterSizeOptions}
                                  value={field.value || undefined}
                                  disabled
                                />
                              )}
                            </Field>
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Hệ số xung
                            </Title>
                            <Field name="pulse_ratio_old">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Tự động điền"
                                  value={field.value || undefined}
                                  disabled>
                                  {PULSE_RATIO_OPTIONS.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Option>
                                  ))}
                                </Select>
                              )}
                            </Field>
                          </Col>
                          <Col md={12} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Chỉ số cực đại
                            </Title>
                            <Field name="max_reading_old">
                              {({ field }) => (
                                <Select
                                  {...field}
                                  style={{ width: '100%' }}
                                  placeholder="Tự động điền"
                                  value={field.value || undefined}
                                  disabled>
                                  {MAX_READING_OPTIONS.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Option>
                                  ))}
                                </Select>
                              )}
                            </Field>
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Chỉ số đồng hồ (lít)*
                            </Title>
                            <Field name="meter_reading_old">
                              {({ field }) => (
                                <Input {...field} placeholder="Nhập chỉ số" disabled={disabled} />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_reading_old"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Ảnh chỉ số đồng hồ*
                            </Title>
                            <Field name="meter_reading_old_image">
                              {({ field }) => (
                                <InputUploadImage
                                  key={componentKey}
                                  {...field}
                                  initValue={replacement?.meter_reading_old_image ?? ''}
                                  onChange={(file) =>
                                    setFieldValue('meter_reading_old_image', file)
                                  }
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_reading_old_image"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                          <Col md={24} xs={24}>
                            <Title style={{ fontSize: '15px', fontWeight: '400' }}>
                              Ảnh seri đồng hồ*
                            </Title>
                            <Field name="meter_serial_image_old">
                              {({ field }) => (
                                <InputUploadImage
                                  key={componentKey}
                                  {...field}
                                  initValue={replacement?.meter_serial_image_old ?? ''}
                                  onChange={(file) => setFieldValue('meter_serial_image_old', file)}
                                  disabled={disabled}
                                />
                              )}
                            </Field>
                            <ErrorMessage
                              name="meter_serial_image_old"
                              component="div"
                              style={{ color: '#e11d48' }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    )}

                    <Col md={16} xs={24}>
                      <Title style={{ fontSize: '15px', fontWeight: '400' }}>Lý do*</Title>
                      <Field name="reason">
                        {({ field }) => (
                          <TextArea
                            disabled={disabled}
                            {...field}
                            rows={4}
                            placeholder="Nhập lý do"
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="reason"
                        component="div"
                        style={{ color: '#e11d48' }}
                        disabled={disabled}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} style={{ marginTop: '32px' }}>
                    <Col>
                      <Space size={16}>
                        <Button
                          type="primary"
                          onClick={() => navigate(-1)}
                          style={{
                            color: '#fff',
                            backgroundColor: '#f59e0b',
                            height: '36px'
                          }}>
                          Huỷ bỏ
                        </Button>
                        {!disabled && (
                          <Button
                            style={{ backgroundColor: '#0284c7', height: '36px' }}
                            type="primary"
                            htmlType="submit"
                            loading={loading}>
                            {'Cập nhật'}
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Col>
                <Col md={8} xs={24}></Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};
